/**
 * Chat Context — Manages chatbot state across the application.
 *
 * Handles:
 * - Message history
 * - Open/closed state of the chat widget
 * - Unread message indicator
 * - Sending messages via tRPC
 */

import React, { createContext, useContext, useState, useCallback, useRef } from 'react';
import { trpc } from '../lib/trpc';

// ============================================================================
// Types
// ============================================================================

export interface ChatAction {
  type: 'navigate' | 'book_appointment' | 'view_records' | 'refill_prescription' | 'start_telehealth';
  label: string;
  route?: string;
  data?: Record<string, unknown>;
}

export interface ChatMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  actions?: ChatAction[];
  timestamp: Date;
}

interface ChatContextType {
  messages: ChatMessage[];
  isOpen: boolean;
  isLoading: boolean;
  hasUnread: boolean;
  toggleChat: () => void;
  openChat: () => void;
  closeChat: () => void;
  sendMessage: (text: string) => Promise<void>;
  clearChat: () => void;
  executeAction: (action: ChatAction) => void;
}

// ============================================================================
// Context
// ============================================================================

const ChatContext = createContext<ChatContextType | null>(null);

export function useChatContext() {
  const ctx = useContext(ChatContext);
  if (!ctx) throw new Error('useChatContext must be used within ChatProvider');
  return ctx;
}

// ============================================================================
// Provider
// ============================================================================

let messageIdCounter = 0;
function generateId() {
  return `msg_${Date.now()}_${++messageIdCounter}`;
}

export function ChatProvider({ children }: { children: React.ReactNode }) {
  const [messages, setMessages] = useState<ChatMessage[]>([
    {
      id: 'welcome',
      role: 'assistant',
      content: "Hey, I'm Jason — your Strix Healthcare assistant. I can help you navigate the app, manage appointments, check records, or answer health questions. What do you need?",
      timestamp: new Date(),
    },
  ]);
  const [isOpen, setIsOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [hasUnread, setHasUnread] = useState(false);
  const isOpenRef = useRef(isOpen);

  // Keep ref in sync
  isOpenRef.current = isOpen;

  const sendMessageMutation = trpc.chat.sendMessage.useMutation();

  const toggleChat = useCallback(() => {
    setIsOpen((prev) => {
      if (!prev) setHasUnread(false); // Clear unread when opening
      return !prev;
    });
  }, []);

  const openChat = useCallback(() => {
    setIsOpen(true);
    setHasUnread(false);
  }, []);

  const closeChat = useCallback(() => {
    setIsOpen(false);
  }, []);

  const sendMessage = useCallback(async (text: string) => {
    const userMessage: ChatMessage = {
      id: generateId(),
      role: 'user',
      content: text,
      timestamp: new Date(),
    };

    setMessages((prev) => [...prev, userMessage]);
    setIsLoading(true);

    try {
      // Build history from previous messages (exclude welcome message)
      const history = messages
        .filter((m) => m.id !== 'welcome')
        .map((m) => ({ role: m.role as 'user' | 'assistant', content: m.content }));

      const response = await sendMessageMutation.mutateAsync({
        message: text,
        history,
      });

      const assistantMessage: ChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: response.message,
        actions: response.actions,
        timestamp: new Date(),
      };

      setMessages((prev) => [...prev, assistantMessage]);

      // If chat is closed, mark unread
      if (!isOpenRef.current) {
        setHasUnread(true);
      }
    } catch (error) {
      const errorMessage: ChatMessage = {
        id: generateId(),
        role: 'assistant',
        content: "Sorry, I hit a snag processing that. Could you try again?",
        timestamp: new Date(),
      };
      setMessages((prev) => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  }, [messages, sendMessageMutation]);

  const clearChat = useCallback(() => {
    setMessages([
      {
        id: 'welcome',
        role: 'assistant',
        content: "Chat cleared. How can I help you?",
        timestamp: new Date(),
      },
    ]);
  }, []);

  const executeAction = useCallback((action: ChatAction) => {
    if (action.route) {
      window.location.href = action.route;
    }
  }, []);

  return (
    <ChatContext.Provider
      value={{
        messages,
        isOpen,
        isLoading,
        hasUnread,
        toggleChat,
        openChat,
        closeChat,
        sendMessage,
        clearChat,
        executeAction,
      }}
    >
      {children}
    </ChatContext.Provider>
  );
}
